import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PrivacyCheckbox from "../components/PrivacyCheckbox";
import { Mail, Lock, User, Phone, ArrowLeft, ArrowRight, Shield, KeyRound } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initialMode);

  // Login flow state
  const [step, setStep] = useState("credentials"); // credentials | otp | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [requestId, setRequestId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Register form
  const [regForm, setRegForm] = useState({ email: "", password: "", confirm: "", name: "", phone: "", privacy: false });

  const { login: googleLogin, loginWithToken } = useAuth();

  const submitLogin1 = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/login-step1`, { email, password });
      setRequestId(res.data.request_id);
      setStep("otp");
      toast.success("A 6-digit code has been emailed to you");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const submitLogin2 = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/login-step2`, { request_id: requestId, otp });
      const { session_token, ...userData } = res.data;
      await loginWithToken(session_token, userData);
      toast.success(`Welcome back, ${userData.name}`);
      const role = userData.role;
      let redirect = "/dashboard";
      if (role === "driver") redirect = "/driver";
      else if (role === "admin" || role === "super_admin") redirect = "/admin";
      navigate(redirect);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (regForm.password !== regForm.confirm) { toast.error("Passwords do not match"); return; }
    if (regForm.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!regForm.privacy) { toast.error("Please accept the Privacy Policy"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/register`, {
        email: regForm.email,
        password: regForm.password,
        name: regForm.name,
        phone: regForm.phone,
        privacy_accepted: regForm.privacy
      });
      toast.success("Account created. Check your email to verify it.");
      setMode("login");
      setEmail(regForm.email);
      setStep("verify-sent");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success("If the email exists, a reset link has been sent.");
      setStep("credentials");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="text-white/50 hover:text-[#C5A059] text-sm flex items-center gap-2 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-8">
          <h1 className="text-3xl font-bold text-white mb-2">TaxiNearMe</h1>
          <p className="text-white/40 text-sm mb-6">Luxury private transfers</p>

          {step !== "otp" && step !== "forgot" && step !== "verify-sent" && (
            <div className="flex border-b border-white/5 mb-6">
              <button onClick={() => { setMode("login"); setStep("credentials"); }}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${mode === "login" ? "text-[#C5A059] border-b-2 border-[#C5A059]" : "text-white/40 hover:text-white/60"}`}
                data-testid="tab-login">
                Sign In
              </button>
              <button onClick={() => { setMode("register"); setStep("credentials"); }}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${mode === "register" ? "text-[#C5A059] border-b-2 border-[#C5A059]" : "text-white/40 hover:text-white/60"}`}
                data-testid="tab-register">
                Register
              </button>
            </div>
          )}

          {step === "verify-sent" && (
            <div className="text-center space-y-4 py-8" data-testid="verify-sent">
              <div className="w-16 h-16 mx-auto bg-[#C5A059]/20 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-[#C5A059]" />
              </div>
              <h2 className="text-white text-xl font-semibold">Check your email</h2>
              <p className="text-white/60 text-sm">We sent a verification link to <strong className="text-white">{regForm.email || email}</strong>. Click it to activate your account.</p>
              <Button onClick={() => { setStep("credentials"); setMode("login"); }} className="btn-gold w-full">
                Got it &mdash; back to sign in
              </Button>
            </div>
          )}

          {step === "credentials" && mode === "login" && (
            <form onSubmit={submitLogin1} className="space-y-4" data-testid="login-form">
              <Field icon={Mail} type="email" placeholder="Email" value={email} onChange={setEmail} testId="login-email" required />
              <Field icon={Lock} type="password" placeholder="Password" value={password} onChange={setPassword} testId="login-password" required />
              <Button type="submit" className="btn-gold w-full" disabled={submitting} data-testid="login-submit">
                {submitting ? "Signing in..." : "Continue"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={() => setStep("forgot")} className="text-white/50 hover:text-[#C5A059]" data-testid="forgot-link">
                  Forgot password?
                </button>
                <button type="button" onClick={() => setMode("register")} className="text-white/50 hover:text-[#C5A059]">
                  Need an account?
                </button>
              </div>
              <Divider />
              <Button type="button" onClick={googleLogin} variant="outline" className="w-full bg-transparent border-white/20 text-white hover:bg-white/5" data-testid="google-login-btn">
                Sign in with Google
              </Button>
            </form>
          )}

          {step === "credentials" && mode === "register" && (
            <form onSubmit={submitRegister} className="space-y-4" data-testid="register-form">
              <Field icon={User} placeholder="Full Name" value={regForm.name} onChange={(v) => setRegForm(p => ({ ...p, name: v }))} testId="reg-name" required />
              <Field icon={Mail} type="email" placeholder="Email" value={regForm.email} onChange={(v) => setRegForm(p => ({ ...p, email: v }))} testId="reg-email" required />
              <Field icon={Phone} type="tel" placeholder="Phone" value={regForm.phone} onChange={(v) => setRegForm(p => ({ ...p, phone: v }))} testId="reg-phone" required />
              <Field icon={Lock} type="password" placeholder="Password (min 8 chars)" value={regForm.password} onChange={(v) => setRegForm(p => ({ ...p, password: v }))} testId="reg-password" required />
              <Field icon={Lock} type="password" placeholder="Confirm password" value={regForm.confirm} onChange={(v) => setRegForm(p => ({ ...p, confirm: v }))} testId="reg-confirm" required />
              <PrivacyCheckbox checked={regForm.privacy} onChange={(v) => setRegForm(p => ({ ...p, privacy: v }))} testId="reg-privacy" />
              <Button type="submit" className="btn-gold w-full" disabled={submitting} data-testid="register-submit">
                {submitting ? "Creating account..." : "Create Account"}
              </Button>
              <Divider />
              <Button type="button" onClick={googleLogin} variant="outline" className="w-full bg-transparent border-white/20 text-white hover:bg-white/5">
                Sign up with Google instead
              </Button>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={submitLogin2} className="space-y-4" data-testid="otp-form">
              <div className="text-center mb-4">
                <KeyRound className="w-12 h-12 mx-auto text-[#C5A059] mb-3" />
                <h2 className="text-white text-lg font-semibold">Two-factor verification</h2>
                <p className="text-white/50 text-sm mt-1">Enter the 6-digit code we sent to <strong className="text-white">{email}</strong></p>
              </div>
              <Input type="text" inputMode="numeric" maxLength={6} pattern="[0-9]{6}"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                className="input-dark text-center text-2xl tracking-widest font-mono"
                placeholder="000000"
                autoFocus
                data-testid="otp-input" />
              <Button type="submit" className="btn-gold w-full" disabled={submitting || otp.length !== 6} data-testid="otp-submit">
                {submitting ? "Verifying..." : "Verify & Sign In"}
              </Button>
              <button type="button" onClick={() => { setStep("credentials"); setOtp(""); }} className="text-white/50 hover:text-white text-xs w-full text-center">
                Use a different account
              </button>
            </form>
          )}

          {step === "forgot" && (
            <form onSubmit={submitForgot} className="space-y-4" data-testid="forgot-form">
              <div className="text-center mb-4">
                <Shield className="w-12 h-12 mx-auto text-[#C5A059] mb-3" />
                <h2 className="text-white text-lg font-semibold">Reset Password</h2>
                <p className="text-white/50 text-sm mt-1">Enter your email and we&apos;ll send you a reset link.</p>
              </div>
              <Field icon={Mail} type="email" placeholder="Email" value={email} onChange={setEmail} testId="forgot-email" required />
              <Button type="submit" className="btn-gold w-full" disabled={submitting} data-testid="forgot-submit">
                {submitting ? "Sending..." : "Send Reset Link"}
              </Button>
              <button type="button" onClick={() => setStep("credentials")} className="text-white/50 hover:text-white text-xs w-full text-center">
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, type = "text", placeholder, value, onChange, testId, required }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
      <Input type={type} placeholder={placeholder} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-dark pl-10" required={required} data-testid={testId} />
    </div>
  );
}

function Divider() {
  return (
    <div className="relative my-2">
      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/5"></div></div>
      <div className="relative flex justify-center text-xs"><span className="bg-[#0F0F0F] px-2 text-white/30">or</span></div>
    </div>
  );
}
